import { createAsyncThunk } from "@reduxjs/toolkit";
import type { Dependencies } from "@redux/store";
import type { AppState } from "@redux/app-state";
import type { ROLES } from "@user-management-context/shared/domain/types/enums/roles";
import type { UserDetailsRoleVM } from "@user-management-context/shared/domain/types/user-details-role-vm";
import { getAccessTokenCookie, getRefreshTokenCookie } from "@utils/access-token-cookie";
import { SESSION_EXPIRED_ERROR_NAME, UNAUTHORIZED_ERROR_NAME } from "@shared-kernel/domain/errors/errors";
import { getOriginalAdminIdFromAccessToken } from "@utils/access-token-jwt";
import { wasImpersonating } from "@shared-kernel/core/impersonation/impersonation-marker";
import { retrieveUserProfile } from "@user-management-context/read/application/use-cases/user-profile/retrieve-user-profile";
import { retrieveAccountRoles } from "@user-management-context/read/application/use-cases/account-roles/retrieve-account-roles";
import { setActiveRole } from "@user-management-context/write/application/use-cases/active-role/set-active-role";
import { getActiveRole, getLastActiveRole, hasRole, restoreActiveRole } from "@shared-kernel/core/active-role/active-role";
import {
  markSessionActive,
  markSessionSyncing,
  markImpersonationExpired,
  markSessionTerminated,
  setImpersonatedBy,
  type SessionTerminationReason,
} from "./session-reducer";

export type SyncSessionTerminationReason = SessionTerminationReason;

export type SyncSessionResult = { status: "active" } | { status: "terminated"; reason: SyncSessionTerminationReason };

export function selectActiveRoleForSync(args: {
  profile: {
    isAdmin: boolean;
    roles: ReadonlyArray<UserDetailsRoleVM>;
    defaultActiveRole: ROLES | null;
  };
  sessionRole: ROLES | null;
  lastRole: ROLES | null;
}): ROLES | null {
  if (args.profile.isAdmin) return null;
  const candidate = args.sessionRole ?? args.lastRole;
  if (candidate && hasRole(args.profile.roles, candidate)) return candidate;
  return args.profile.defaultActiveRole ?? args.profile.roles[0]?.role ?? null;
}

// A session is only ever destroyed on an explicit verdict from the server. The two
// named errors below are ones we minted ourselves in the interceptor from a status
// code (401/440), so they carry such a verdict. Anything else — a rate limit, a 5xx,
// a network outage, an unexpected throw — means we got no usable answer, which is not
// the same as "the session is over": ending it there would discard a refresh token
// the server still honours and force a pointless re-login.
//
// Hence the conservative default: an error nobody classified lands in "unavailable"
// and costs the user nothing more than a retry.
const resolveTerminationReason = (errorName: string | undefined): SyncSessionTerminationReason => {
  if (errorName === SESSION_EXPIRED_ERROR_NAME) return "session-expired";
  if (errorName === UNAUTHORIZED_ERROR_NAME) return "auth-error";
  return "unavailable";
};

export const syncSession = createAsyncThunk<SyncSessionResult, void, { state: AppState; extra: Partial<Dependencies> }>(
  "session/sync",
  async (_: void, { dispatch, getState }) => {
    dispatch(markSessionSyncing());

    try {
      // With a short-lived access token, the AT cookie can be gone (expired) while
      // a valid refresh token remains. Only give up when both are absent; otherwise
      // proceed — the first authenticated request's 401 triggers the interceptor's
      // refresh-and-retry, which re-hydrates the access token.
      const token = getAccessTokenCookie();
      if (!token && !getRefreshTokenCookie()) {
        dispatch(markSessionTerminated("no-token"));
        return { status: "terminated", reason: "no-token" };
      }

      const result = await dispatch(retrieveUserProfile());
      if (retrieveUserProfile.rejected.match(result)) {
        // Not every failed profile fetch means the session is over. A 429/5xx/network
        // failure on /refresh makes the interceptor rethrow the original error on
        // purpose, session and refresh token untouched — calling that a termination
        // would throw away a refresh token the server still honours. Only a named auth
        // error ends the session; the reason also picks the message the user gets.
        const reason: SyncSessionTerminationReason = resolveTerminationReason(result.error.name);
        dispatch(markSessionTerminated(reason));
        return { status: "terminated", reason };
      }

      const profile = getState().shared.session.user;
      if (!profile) {
        dispatch(markSessionTerminated("auth-error"));
        return { status: "terminated", reason: "auth-error" };
      }

      const originalAdminId = getOriginalAdminIdFromAccessToken();
      // An impersonation access token has a hard expiry and no refresh token of its
      // own; when it dies its cookie simply vanishes, and the profile fetched just
      // above came back as the admin — the interceptor re-minted an admin access
      // token from the admin refresh token that never left. Nothing in the session
      // says so, hence the marker: it survives the token and the page reload, so
      // "no originalAdminId, yet we were impersonating" is the expiry.
      //
      // Raising the flag is all this does: clearing the marker belongs to whoever
      // actually shows the notice (useImpersonation). Erasing it here would destroy
      // the only durable evidence before anything was said — and a termination
      // right after, or a reload during the router's lazy load, resets the flag —
      // leaving the admin never told. Left standing, the next sync raises it again.
      if (!originalAdminId && wasImpersonating()) dispatch(markImpersonationExpired());
      dispatch(setImpersonatedBy(originalAdminId ? { adminId: originalAdminId } : null));

      if (!profile.isAdmin) await dispatch(retrieveAccountRoles());

      const role = selectActiveRoleForSync({
        profile,
        sessionRole: getActiveRole(),
        lastRole: getLastActiveRole(),
      });
      dispatch(setActiveRole(role));
      restoreActiveRole(role);

      dispatch(markSessionActive({ lastSyncedAt: Date.now() }));
      return { status: "active" };
    } catch (error) {
      // Never leave the session stuck in "syncing": the condition below keys off
      // that status, so a stuck one would block every later sync for good. An
      // unexpected throw is not a verdict on the session either — hence
      // "unavailable", which leaves the stored tokens alone.
      dispatch(markSessionTerminated("unavailable"));
      throw error;
    }
  },
  {
    // Single-flight, same rule as the refresh coordinator next door: a burst of
    // failing requests wakes the auth listener once per rejected thunk, and each
    // would otherwise probe the session on its own (profile + roles per error).
    // One answer to "is this session still valid?" serves the whole burst.
    condition: (_: void, { getState }) => getState().shared.session.status !== "syncing",
  },
);
